import { useMemo } from 'react';
import {
  SelectOption,
  SelectAndRevealOptionType,
  DeliveryOption,
  PaymentOption,
  OptionParams,
} from 'typescript/components/select_and_reveal/types';
import ShippingBox from '@square-icons/react/24/Misc/shipping-box';
import { ReactComponent as Store } from 'images/icons/store.svg';
import ShipToMe from 'typescript/apps/checkout_v3/components/delivery/ship_to_me/ship_to_me';
import PickUpInStore from 'typescript/apps/checkout_v3/components/delivery/bopis/pick_up_in_store';
import PayInFull from 'typescript/apps/checkout_v3/components/payment/pay_in_full/pay_in_full';
import PayInInstallments from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/pay_in_installments';
import t from 'typescript/utilities/translate';

interface useSelectAndRevealProps {
  optionType: SelectAndRevealOptionType;
  options: OptionParams[];
}

interface UseSelectAndReveal {
  selectOptions: SelectOption[];
}

const buildShippingOption = (): SelectOption => {
  return {
    option: DeliveryOption.SHIP_TO_ME,
    icon: <ShippingBox />,
    title: t('checkout.delivery.title.shipToMe'),
    subtext: t('checkout.delivery.subtitle.shipToMe'),
    revealComponent: <ShipToMe />,
  };
};

const buildBopisOption = (): SelectOption => {
  return {
    option: DeliveryOption.BOPIS,
    icon: <Store />,
    title: 'Pick up in store',
    subtext: 'Available today at Best Buy',
    revealComponent: <PickUpInStore className="shop-grid-item-full" />,
  };
};

const buildPayInFullOption = (overrides = null): SelectOption => {
  return {
    option: PaymentOption.PAY_IN_FULL,
    icon: null,
    title: t('checkout.payment_v3.payInFull.title'),
    subtext: t('checkout.payment_v3.payInFull.subtitle'),
    revealComponent: <PayInFull className="shop-grid-item-full" />,
    sideLabel: overrides?.sideLabel ?? '',
  };
};

const buildPayInInstallmentsOption = (overrides = null): SelectOption => {
  return {
    option: PaymentOption.PAY_IN_INSTALLMENTS,
    icon: null,
    title: t('checkout.payment_v3.payInInstallments.title'),
    subtext: overrides?.subtext ?? '',
    revealComponent: <PayInInstallments className="shop-grid-item-full" />,
    sideLabel: overrides?.sideLabel ?? '',
  };
};

const optionBuilders = {
  [DeliveryOption.SHIP_TO_ME]: buildShippingOption,
  [DeliveryOption.BOPIS]: buildBopisOption,
  [PaymentOption.PAY_IN_FULL]: buildPayInFullOption,
  [PaymentOption.PAY_IN_INSTALLMENTS]: buildPayInInstallmentsOption,
};

const createOptionsArray = (
  optionType: SelectAndRevealOptionType,
  selectOptions: OptionParams[],
): SelectOption[] => {
  switch (optionType) {
    case SelectAndRevealOptionType.DELIVERY: {
      return (selectOptions as OptionParams[]).map(({ option }) =>
        optionBuilders[option](),
      );
    }
    case SelectAndRevealOptionType.PAYMENT: {
      return (selectOptions as OptionParams[]).map(({ option, overrides }) =>
        overrides
          ? optionBuilders[option](overrides)
          : optionBuilders[option](),
      );
    }
    default: {
      return [];
    }
  }
};

const useSelectAndReveal = ({
  optionType,
  options,
}: useSelectAndRevealProps): UseSelectAndReveal => {
  const selectOptions = useMemo(() => {
    return createOptionsArray(optionType, options);
  }, [optionType, options]);

  return { selectOptions };
};

export default useSelectAndReveal;
